import { useEffect, useMemo, useRef, useState } from 'react';
import { db, reference, type LocalPlace, type LocalStop, type PendingMedia } from '../lib/localdb.js';
import { addCapture, addParts, addReadings, saveAsset } from '../lib/actions.js';
import { previewUrl } from '../lib/media.js';
import { captureLabel, type CaptureKind, type RequirementStatus } from '../lib/stop.js';
import { text } from '../lib/format.js';
import { BigButton, CameraButton, Chip, Keypad, Sheet, SignaturePad } from '../components/Controls.js';
import { scan, scanningSupported } from '../lib/barcode.js';
import { VoiceRecorder, recordingSupported, tone } from '../lib/audio.js';
import { HazardSheet } from './Hazard.js';

/**
 * Doing the work.
 *
 * Everything a technician records at the stop, in the order the service asked for it. The
 * screen is a list of the service's own requirements — *before, after, airflow before, airflow
 * after* — because a technician should never be guessing what the paperwork wants, and because
 * an untyped pile of photographs is what the import produced: 27 229 images and not one of
 * them says which shows a problem and which shows the fix.
 */

type Product = { id: string; code: string | null; name: unknown; unitPrice: string; unitOfMeasure: string | null };
type DefaultPart = { serviceId: string; productId: string; quantity: number };
type AssetType = { id: string; key: string; name: unknown };
type Requirement = { id: string; name: unknown; equipmentNote: unknown; isHeight: boolean; isConfinedSpace: boolean };

export function WorkPanel({
  stop,
  place,
  requirements,
  onChanged,
}: {
  stop: LocalStop;
  place: LocalPlace | null;
  requirements: RequirementStatus[];
  onChanged: () => void | Promise<void>;
}) {
  const [captureKinds, setCaptureKinds] = useState<CaptureKind[]>([]);
  const [media, setMedia] = useState<PendingMedia[]>([]);
  const [readingFor, setReadingFor] = useState<RequirementStatus | null>(null);
  const [signatureFor, setSignatureFor] = useState<RequirementStatus | null>(null);
  const [hazardOpen, setHazardOpen] = useState(false);
  const [partsOpen, setPartsOpen] = useState(false);
  const [assetOpen, setAssetOpen] = useState(false);
  const [noteOpen, setNoteOpen] = useState(false);

  useEffect(() => {
    const load = async () => {
      setCaptureKinds(await reference<CaptureKind[]>('captureKinds', []));
      setMedia((await (await db()).getAllFromIndex('media', 'byAppointment', stop.id)) ?? []);
    };
    void load();
  }, [stop.id, stop.captures.length]);

  const photos = requirements.filter((requirement) => requirement.type === 'photo');
  const readings = requirements.filter((requirement) => requirement.type === 'reading');
  const signatures = requirements.filter((requirement) => requirement.type === 'signature');
  const confirmations = requirements.filter((requirement) => requirement.type === 'confirmation');

  const take = async (kind: string, file: File) => {
    await addCapture({ appointmentId: stop.id, kind, blob: file, withPosition: true });
    await onChanged();
  };

  return (
    <div className="work">
      {/* What the service asked for, in the order it asked. */}
      {photos.length > 0 && (
        <section className="work__block">
          <h3>Photos demandées</h3>
          <div className="work__grid">
            {photos.map((requirement) => (
              <CameraButton
                key={requirement.id}
                tone={requirement.done ? 'quiet' : 'primary'}
                onFile={(file) => void take(requirement.key, file)}
              >
                {requirement.done ? `✓ ${requirement.title}` : requirement.title}
              </CameraButton>
            ))}
          </div>
        </section>
      )}

      {readings.length > 0 && (
        <section className="work__block">
          <h3>Relevés</h3>
          <div className="work__grid">
            {readings.map((requirement) => (
              <BigButton
                key={requirement.id}
                tone={requirement.outOfSpec ? 'danger' : requirement.done ? 'quiet' : 'primary'}
                onClick={() => setReadingFor(requirement)}
                hint={requirement.unit ?? undefined}
              >
                {requirement.done ? `✓ ${requirement.title}` : requirement.title}
              </BigButton>
            ))}
          </div>
          {stop.readings.length > 0 && <ReadingList stop={stop} />}
        </section>
      )}

      {confirmations.length > 0 && (
        <section className="work__block">
          <h3>À confirmer</h3>
          <div className="chips">
            {confirmations.map((requirement) => (
              <Chip key={requirement.id} tone={requirement.done ? 'positive' : 'neutral'}>
                {requirement.title}
              </Chip>
            ))}
          </div>
        </section>
      )}

      {/* Everything the app behaves on, always available whatever the service declared. */}
      <section className="work__block">
        <h3>Autres photos</h3>
        <div className="work__grid">
          <CameraButton tone="quiet" onFile={(file) => void take('before', file)}>
            Avant
          </CameraButton>
          <CameraButton tone="quiet" onFile={(file) => void take('after', file)}>
            Après
          </CameraButton>
          <CameraButton tone="quiet" onFile={(file) => void take('defect', file)}>
            Défaut
          </CameraButton>
          <CameraButton tone="quiet" onFile={(file) => void take('access', file)}>
            Accès
          </CameraButton>
          <CameraButton tone="quiet" onFile={(file) => void take('document', file)}>
            Document du client
          </CameraButton>
          {captureKinds.map((kind) => (
            <CameraButton key={kind.id} tone="quiet" onFile={(file) => void take(kind.key, file)}>
              {text(kind.name)}
            </CameraButton>
          ))}
        </div>
      </section>

      <section className="work__block work__block--actions">
        <BigButton tone="danger" onClick={() => setHazardOpen(true)} hint="Photo et note obligatoires">
          Signaler un danger
        </BigButton>
        <BigButton tone="quiet" onClick={() => setAssetOpen(true)}>
          Plaque signalétique
        </BigButton>
        <BigButton tone="quiet" onClick={() => setPartsOpen(true)}>
          Pièces utilisées
        </BigButton>
        {recordingSupported() && (
          <BigButton tone="quiet" onClick={() => setNoteOpen(true)}>
            Note vocale
          </BigButton>
        )}
        {signatures.map((requirement) => (
          <BigButton
            key={requirement.id}
            tone={requirement.done ? 'quiet' : 'primary'}
            onClick={() => setSignatureFor(requirement)}
          >
            {requirement.done ? `✓ ${requirement.title}` : requirement.title}
          </BigButton>
        ))}
      </section>

      <Gallery stop={stop} media={media} captureKinds={captureKinds} />

      <ReadingSheet
        requirement={readingFor}
        stop={stop}
        onClose={() => setReadingFor(null)}
        onSaved={async () => {
          setReadingFor(null);
          await onChanged();
        }}
      />

      <Sheet open={Boolean(signatureFor)} title={signatureFor?.title ?? 'Signature'} onClose={() => setSignatureFor(null)}>
        {signatureFor && (
          <SignaturePad
            label={`Signé en présence du client · ${stop.customer.name}`}
            onDone={async (blob) => {
              await addCapture({
                appointmentId: stop.id,
                kind: signatureFor.key,
                blob,
                caption: stop.customer.name,
                withPosition: true,
              });
              setSignatureFor(null);
              await onChanged();
            }}
          />
        )}
      </Sheet>

      <HazardSheet
        open={hazardOpen}
        stop={stop}
        onClose={() => setHazardOpen(false)}
        onSaved={async () => {
          setHazardOpen(false);
          await onChanged();
        }}
      />

      <PartsSheet
        open={partsOpen}
        stop={stop}
        onClose={() => setPartsOpen(false)}
        onSaved={async () => {
          setPartsOpen(false);
          await onChanged();
        }}
      />

      <AssetSheet
        open={assetOpen}
        stop={stop}
        place={place}
        onClose={() => setAssetOpen(false)}
        onSaved={async () => {
          setAssetOpen(false);
          await onChanged();
        }}
      />

      <VoiceNoteSheet
        open={noteOpen}
        stop={stop}
        onClose={() => setNoteOpen(false)}
        onSaved={async () => {
          setNoteOpen(false);
          await onChanged();
        }}
      />
    </div>
  );
}

/** What has been read so far, with anything out of range said plainly. */
function ReadingList({ stop }: { stop: LocalStop }) {
  return (
    <ul className="readings">
      {stop.readings.map((reading) => (
        <li key={reading.id} className={reading.outOfSpec ? 'out' : ''}>
          <span className="readings__key">
            {reading.key}
            {reading.phase !== 'single' ? ` · ${reading.phase === 'before' ? 'avant' : 'après'}` : ''}
            {reading.derived ? ' · calculé' : ''}
          </span>
          <span className="readings__value tnum">
            {reading.value}
            {reading.unit ? ` ${reading.unit}` : ''}
          </span>
          {reading.outOfSpec && <span className="readings__flag">hors norme</span>}
          {reading.pending && <span className="badge badge--pending">non envoyé</span>}
        </li>
      ))}
    </ul>
  );
}

/**
 * Entering a reading.
 *
 * A large numeric keypad and the instrument photograph in the same gesture. The photograph is
 * the proof and the number is the data — the same relationship a voice note has to its
 * transcript — which is why both are offered here rather than one replacing the other.
 */
function ReadingSheet({
  requirement,
  stop,
  onClose,
  onSaved,
}: {
  requirement: RequirementStatus | null;
  stop: LocalStop;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [phase, setPhase] = useState<'before' | 'after' | 'single'>('single');
  const [locationLabel, setLocationLabel] = useState('');
  const [mediaId, setMediaId] = useState<string | null>(null);

  useEffect(() => {
    setValue('');
    setLocationLabel('');
    setMediaId(null);
    setPhase(requirement?.phase === 'both' ? (requirement.before ? 'after' : 'before') : (requirement?.phase as any) ?? 'single');
  }, [requirement?.id]);

  if (!requirement) return null;

  const numeric = Number(value.replace(',', '.'));
  const valid = value.length > 0 && Number.isFinite(numeric);
  const out = valid && ((requirement.min != null && numeric < requirement.min) || (requirement.max != null && numeric > requirement.max));

  return (
    <Sheet open title={requirement.title} onClose={onClose}>
      <div className="reading">
        <p className="reading__value tnum">
          {value || '—'} <span>{requirement.unit ?? ''}</span>
        </p>
        {out && (
          <p className="reading__out" role="alert">
            Hors de la plage {requirement.min ?? '−∞'} à {requirement.max ?? '∞'} {requirement.unit ?? ''}
          </p>
        )}

        {requirement.phase === 'both' && (
          <div className="chips">
            <Chip on={phase === 'before'} onToggle={() => setPhase('before')}>
              Avant
            </Chip>
            <Chip on={phase === 'after'} onToggle={() => setPhase('after')}>
              Après
            </Chip>
          </div>
        )}

        <Keypad value={value} onChange={setValue} />

        <label className="field">
          <span>Où</span>
          <input
            value={locationLabel}
            onChange={(event) => setLocationLabel(event.target.value)}
            placeholder="Registre du salon, sortie de sécheuse…"
          />
        </label>

        <CameraButton
          tone={mediaId ? 'quiet' : 'warm'}
          onFile={async (file) => {
            const saved = await addCapture({ appointmentId: stop.id, kind: 'reading', blob: file });
            setMediaId(saved.id);
          }}
        >
          {mediaId ? '✓ Photo de l’instrument' : 'Photo de l’instrument'}
        </CameraButton>

        <BigButton
          tone="positive"
          disabled={!valid}
          onClick={async () => {
            await addReadings(stop.id, [
              {
                key: requirement.key,
                value: numeric,
                unit: requirement.unit,
                phase,
                locationLabel: locationLabel || null,
                mediaId,
                min: requirement.min,
                max: requirement.max,
              },
            ]);
            await onSaved();
          }}
        >
          Enregistrer
        </BigButton>
      </div>
    </Sheet>
  );
}

/**
 * Parts consumed.
 *
 * The default kit first, because the common case is a confirmation rather than a search
 * through 176 catalogue items with gloves on. The scanner second, for anything with a barcode.
 * Free text last, because a part recorded badly beats a part not recorded.
 */
function PartsSheet({
  open,
  stop,
  onClose,
  onSaved,
}: {
  open: boolean;
  stop: LocalStop;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [products, setProducts] = useState<Product[]>([]);
  const [defaults, setDefaults] = useState<DefaultPart[]>([]);
  const [chosen, setChosen] = useState<Map<string, number>>(new Map());
  const [scanned, setScanned] = useState<Array<{ code: string; label: string }>>([]);
  const videoRef = useRef<HTMLVideoElement>(null);
  const [scanning, setScanning] = useState(false);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      setProducts(await reference<Product[]>('products', []));
      setDefaults(await reference<DefaultPart[]>('defaultParts', []));
    })();
  }, [open]);

  const kit = useMemo(() => {
    const serviceIds = new Set(stop.services.map((service) => service.id));
    return defaults
      .filter((part) => serviceIds.has(part.serviceId))
      .map((part) => ({ part, product: products.find((candidate) => candidate.id === part.productId) }))
      .filter((row): row is { part: DefaultPart; product: Product } => Boolean(row.product));
  }, [defaults, products, stop.services]);

  useEffect(() => {
    if (!scanning || !videoRef.current) return;
    let stop_: (() => void) | undefined;
    let stream: MediaStream | undefined;

    void (async () => {
      try {
        stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
        if (!videoRef.current) return;
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
        stop_ = await scan(videoRef.current, (code) => {
          const product = products.find((candidate) => candidate.code === code);
          setScanned((current) =>
            current.some((row) => row.code === code)
              ? current
              : [...current, { code, label: product ? text(product.name) : code }],
          );
        });
      } catch {
        setScanning(false);
      }
    })();

    return () => {
      stop_?.();
      for (const track of stream?.getTracks() ?? []) track.stop();
    };
  }, [scanning, products]);

  const total = chosen.size + scanned.length;

  return (
    <Sheet open={open} title="Pièces utilisées" onClose={onClose}>
      {kit.length > 0 && (
        <>
          <h4>Trousse habituelle</h4>
          <div className="chips">
            {kit.map(({ part, product }) => (
              <Chip
                key={product.id}
                on={chosen.has(product.id)}
                onToggle={() =>
                  setChosen((current) => {
                    const next = new Map(current);
                    if (next.has(product.id)) next.delete(product.id);
                    else next.set(product.id, part.quantity);
                    return next;
                  })
                }
              >
                {text(product.name)}
              </Chip>
            ))}
          </div>
        </>
      )}

      {scanningSupported() ? (
        <>
          <BigButton tone="quiet" onClick={() => setScanning((current) => !current)}>
            {scanning ? 'Arrêter le lecteur' : 'Lire un code-barres'}
          </BigButton>
          {scanning && <video ref={videoRef} className="scanner" playsInline muted />}
        </>
      ) : (
        <p className="quiet">Ce navigateur ne lit pas les codes-barres. Choisissez dans la liste.</p>
      )}

      {scanned.length > 0 && (
        <ul className="scanned">
          {scanned.map((row) => (
            <li key={row.code} className="tnum">
              {row.label}
            </li>
          ))}
        </ul>
      )}

      <BigButton
        tone="positive"
        disabled={total === 0}
        onClick={async () => {
          await addParts(stop.id, [
            ...[...chosen.entries()].map(([productId, quantity]) => ({ productId, quantity })),
            // A code the catalogue does not know is still recorded, with the code beside it:
            // refusing it means the technician records nothing at all.
            ...scanned.map((row) => ({
              scannedCode: row.code,
              productId: products.find((product) => product.code === row.code)?.id ?? null,
              label: row.label,
              quantity: 1,
            })),
          ]);
          setChosen(new Map());
          setScanned([]);
          await onSaved();
        }}
      >
        Enregistrer {total > 0 ? `(${total})` : ''}
      </BigButton>
    </Sheet>
  );
}

/**
 * The equipment record, created by the scan.
 *
 * A nameplate photograph and whatever the camera could read off it. Not a form: a form in a
 * dark mechanical room with gloves on is a form nobody fills in, which is exactly why no
 * equipment record exists in the imported data.
 */
function AssetSheet({
  open,
  stop,
  place,
  onClose,
  onSaved,
}: {
  open: boolean;
  stop: LocalStop;
  place: LocalPlace | null;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const [types, setTypes] = useState<AssetType[]>([]);
  const [requirements, setRequirements] = useState<Requirement[]>([]);
  const [assetId, setAssetId] = useState<string | null>(null);
  const [typeId, setTypeId] = useState<string | null>(null);
  const [brand, setBrand] = useState('');
  const [model, setModel] = useState('');
  const [serial, setSerial] = useState('');
  const [where, setWhere] = useState('');
  const [chips, setChips] = useState<Set<string>>(new Set());
  const [mediaId, setMediaId] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    void (async () => {
      setTypes(await reference<AssetType[]>('assetTypes', []));
      setRequirements(await reference<Requirement[]>('accessRequirements', []));
    })();
  }, [open]);

  const pick = (id: string) => {
    const asset = place?.assets.find((candidate) => candidate.id === id);
    if (!asset) return;
    setAssetId(asset.id);
    setTypeId(asset.assetTypeId);
    setBrand(asset.brand ?? '');
    setModel(asset.model ?? '');
    setSerial(asset.serial ?? '');
    setWhere(asset.locationLabel ?? '');
    setChips(new Set(asset.requirements.map((requirement) => requirement.requirementId)));
  };

  return (
    <Sheet open={open} title="Équipement" onClose={onClose}>
      {place && place.assets.length > 0 && (
        <>
          <h4>Corriger un équipement connu</h4>
          <div className="chips">
            {place.assets.map((asset) => (
              <Chip key={asset.id} on={assetId === asset.id} onToggle={() => pick(asset.id)}>
                {[text(types.find((type) => type.id === asset.assetTypeId)?.name), asset.brand, asset.model]
                  .filter(Boolean)
                  .join(' ') || 'Équipement'}
              </Chip>
            ))}
          </div>
        </>
      )}

      <CameraButton
        tone={mediaId ? 'quiet' : 'primary'}
        onFile={async (file) => {
          const saved = await addCapture({ appointmentId: stop.id, kind: 'nameplate', blob: file });
          setMediaId(saved.id);
        }}
      >
        {mediaId ? '✓ Plaque photographiée' : 'Photographier la plaque'}
      </CameraButton>

      <div className="chips">
        {types.map((type) => (
          <Chip key={type.id} on={typeId === type.id} onToggle={() => setTypeId(type.id)}>
            {text(type.name)}
          </Chip>
        ))}
      </div>

      <label className="field">
        <span>Marque</span>
        <input value={brand} onChange={(event) => setBrand(event.target.value)} />
      </label>
      <label className="field">
        <span>Modèle</span>
        <input value={model} onChange={(event) => setModel(event.target.value)} />
      </label>
      <label className="field">
        <span>N° de série</span>
        <input className="tnum" value={serial} onChange={(event) => setSerial(event.target.value)} />
      </label>
      <label className="field">
        <span>Où il se trouve</span>
        <input value={where} onChange={(event) => setWhere(event.target.value)} placeholder="Toit, côté nord" />
      </label>

      <h4>Comment l’atteindre</h4>
      <div className="chips">
        {requirements.map((requirement) => (
          <Chip
            key={requirement.id}
            tone={requirement.isHeight || requirement.isConfinedSpace ? 'warm' : 'neutral'}
            on={chips.has(requirement.id)}
            onToggle={() =>
              setChips((current) => {
                const next = new Set(current);
                if (next.has(requirement.id)) next.delete(requirement.id);
                else next.add(requirement.id);
                return next;
              })
            }
          >
            {text(requirement.name)}
          </Chip>
        ))}
      </div>

      <BigButton
        tone="positive"
        disabled={!mediaId && !assetId && !serial}
        onClick={async () => {
          await saveAsset(stop.id, {
            id: assetId,
            assetTypeId: typeId,
            brand: brand || null,
            model: model || null,
            serial: serial || null,
            locationLabel: where || null,
            nameplateMediaId: mediaId,
            requirementIds: [...chips],
          });
          await onSaved();
        }}
      >
        Enregistrer l’équipement
      </BigButton>
    </Sheet>
  );
}

/**
 * A spoken note.
 *
 * Typing with gloves on in a cold mechanical room does not happen. The recording is kept
 * alongside whatever text eventually comes back, because a transcript is an interpretation and
 * the technician's actual voice is the record.
 *
 * Until a transcription path is chosen in production the note shows as *à transcrire*, which
 * is honest — the alternative would be a plausible sentence nobody said.
 */
function VoiceNoteSheet({
  open,
  stop,
  onClose,
  onSaved,
}: {
  open: boolean;
  stop: LocalStop;
  onClose: () => void;
  onSaved: () => void | Promise<void>;
}) {
  const recorder = useRef<VoiceRecorder | null>(null);
  const [running, setRunning] = useState(false);
  const [warned, setWarned] = useState(false);
  const [elapsed, setElapsed] = useState(0);

  useEffect(() => {
    if (!running) return;
    const timer = window.setInterval(() => setElapsed((value) => value + 1), 1000);
    return () => window.clearInterval(timer);
  }, [running]);

  const start = async () => {
    recorder.current = new VoiceRecorder();
    await recorder.current.start();
    setWarned(!recorder.current.wakeLockHeld);
    setElapsed(0);
    setRunning(true);
    tone('start');
  };

  const stopRecording = async () => {
    const result = await recorder.current?.stop();
    setRunning(false);
    tone('stop');
    if (!result) return;
    await addCapture({
      appointmentId: stop.id,
      kind: 'voice_note',
      blob: result.blob,
      durationSeconds: result.durationSeconds,
    });
    await onSaved();
  };

  return (
    <Sheet open={open} title="Note vocale" onClose={onClose}>
      <p className="quiet">
        Dictez. Rien à regarder, rien à toucher : une tonalité au départ et une à l’arrêt.
      </p>
      {warned && (
        <p className="warn" role="alert">
          Cet appareil peut interrompre l’enregistrement si l’écran s’éteint. Gardez l’écran allumé.
        </p>
      )}
      <p className="reading__value tnum">{running ? `${elapsed} s` : '—'}</p>
      {running ? (
        <BigButton tone="danger" onClick={() => void stopRecording()}>
          Arrêter
        </BigButton>
      ) : (
        <BigButton onClick={() => void start()}>Commencer</BigButton>
      )}
    </Sheet>
  );
}

/** Everything captured at this stop, including what has not left the phone yet. */
function Gallery({ stop, media, captureKinds }: { stop: LocalStop; media: PendingMedia[]; captureKinds: CaptureKind[] }) {
  const [urls, setUrls] = useState<Map<string, string>>(new Map());

  useEffect(() => {
    const next = new Map<string, string>();
    for (const item of media) next.set(item.id, previewUrl(item));
    setUrls(next);
    return () => {
      for (const url of next.values()) URL.revokeObjectURL(url);
    };
  }, [media]);

  if (!stop.captures.length) return null;

  return (
    <section className="work__block">
      <h3>Capté à cet arrêt</h3>
      <ul className="gallery">
        {stop.captures.map((capture) => (
          <li key={capture.id} className={capture.pending ? 'gallery__pending' : ''}>
            {urls.get(capture.id) ? (
              <img src={urls.get(capture.id)} alt={captureLabel(capture.kind, captureKinds)} />
            ) : (
              <span className="gallery__kind">{captureLabel(capture.kind, captureKinds)}</span>
            )}
            <span className="gallery__label">
              {captureLabel(capture.kind, captureKinds)}
              {capture.kind === 'voice_note' && !capture.transcript ? ' · à transcrire' : ''}
              {capture.pending ? ' · non envoyé' : ''}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}
